import { map } from 'lodash';
import type { Dispatch } from 'react';
import { useState } from 'react';

import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { JoinButtonWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicActions/JoinButton';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

interface JoinGroupButtonProps {
  isBackOffice: boolean;
  isConnected: boolean;
  labelButton: string;
  labelA11yButton: string;
  setDisplayRequestModal: Dispatch<boolean>;
  setTopics: Dispatch<ITopic[]>;
  topic: ITopic;
  topics: ITopic[];
}

// implementation inherited from app/assets/javascripts/react/shared/apps/TopicsIndexV2/components/TopicActions/JoinButton.jsx
export const JoinGroupButton = ({
  isBackOffice,
  isConnected,
  labelButton,
  labelA11yButton,
  setDisplayRequestModal,
  setTopics,
  topic,
  topics,
}: JoinGroupButtonProps) => {
  const {
    automatic_validation: automaticValidation,
    custom_attributes_to_fill: customAttributesToFill,
    is_public_and_requires_user_to_fill_custom_attributes_to_join:
      hasCustomAttributesAndIsPublic,
    restricted_access: restrictedAccess,
  } = topic;

  const [isPendingJoinPublicTopic, setIsPendingJoinPublicTopic] =
    useState(false);
  const isDisabledJoin = topic?.joined_at !== null;

  const hasRestrictedAccessWithoutAutomaticValidation =
    restrictedAccess && !automaticValidation;

  const hasCustomAttributesToFillWithAutomaticValidation =
    automaticValidation && customAttributesToFill;

  const hasAutomaticValidationWithoutCustomAttributes =
    automaticValidation && !customAttributesToFill;

  const handleClickJoin = () => {
    if (isBackOffice) return;
    if (
      hasRestrictedAccessWithoutAutomaticValidation ||
      hasCustomAttributesAndIsPublic ||
      hasCustomAttributesToFillWithAutomaticValidation
    ) {
      setDisplayRequestModal(true);
    } else {
      setIsPendingJoinPublicTopic(true);
      const route = hasAutomaticValidationWithoutCustomAttributes
        ? Routes.topics_topic_approvals_path(topic.id)
        : Routes.topics_topic_followings_path(topic.id);

      const approvalParams = hasAutomaticValidationWithoutCustomAttributes
        ? {
            approval: { message: __('Automatically Approved') },
          }
        : {};

      axios
        .post(route, approvalParams)
        .then(
          ({
            data,
          }: {
            data: {
              approval?: {
                id: number;
                created_at: Date;
                auto_approval_user_list_id: number[];
              };
              following?: ITopic;
            };
          }) => {
            const newTopics: ITopic[] = map(topics, (oldTopic) => {
              if (oldTopic.id === topic.id) {
                if (data.following) {
                  return {
                    ...oldTopic,
                    approval_id: data.following.id,
                    joined_at: new Date(),
                  };
                }
              }

              return oldTopic;
            });

            setTopics(newTopics);
          },
        )
        .catch((error) => {
          displayXhrError(error);
        })
        .finally(() => {
          setIsPendingJoinPublicTopic(false);
        });
    }
  };

  return (
    <JoinButtonWithoutContext
      handleJoinClick={handleClickJoin}
      isConnectedUser={isConnected}
      isDisabled={isDisabledJoin}
      isLoading={isPendingJoinPublicTopic}
      labelButton={labelButton}
      labelA11yButton={labelA11yButton}
      topic={topic}
    />
  );
};
